import * as ng from 'angular';
import * as _ from 'underscore';

import {EditorModel} from '../../editor/editor.model';
import {EditorPageModel} from '../../editor/editor-page.model';
import { ExperienceTypeDef, Experience } from '../..';
import { L10nService } from '../../../../shared';

import './email-test-dialog.controller';

const settingsViewUrl = require('./email-settings.html');

ng.module('e360')
   .factory('emailEditorModelFactory', emailEditorModelFactory);

emailEditorModelFactory.$inject = ['instanceServiceFactory'];
function emailEditorModelFactory(instanceServiceFactory) {
   return instanceServiceFactory(EmailEditorModel);
}

export interface EmailEditorModelFactory {
   ({campaign, typeDef:ExperienceTypeDef, experience:Experience}):EmailEditorModel
}

export class EmailEditorModel extends EditorModel {
   readonly workspaceClass = 'email';
   readonly pages: EmailPageModel[];
   readonly settingsView = settingsViewUrl;
   templates:{[lang:string]:string} = {};

   viewOptions:{
      venueType?:string,
      showEventBox:boolean
   } = { showEventBox: true };

   static $inject = [
      'session', 'gettextCatalog', 'emailApi', 'editorCodeParser',
      'toast', 'l10n', '$templateCache', 'experiencesService', 'openEmailTestDialog']
      // separating instance args from injected services
      .concat(['campaign', 'typeDef', 'experience']);

   constructor(
      private session,
      gettextCatalog,
      private emailApi,
      private editorCodeParser,
      private toast,
      l10n:L10nService,
      private $templateCache:ng.ITemplateCacheService,
      private experiencesService,
      private openEmailTestDialog,
      campaign, typeDef:ExperienceTypeDef, experience:Experience
   ) {
      super(campaign, typeDef, experience);

      gettextCatalog.getString('RSVP Confirmation');
      gettextCatalog.getString('You Are Invited!');
      gettextCatalog.getString('Your Event Reminder');
      gettextCatalog.getString('Your Virtual Event Link');
      gettextCatalog.getString('Thank You For Attending');
      gettextCatalog.getString('Sorry We Missed You');
      gettextCatalog.getString('Your Event Media');
      gettextCatalog.getString('You Have Registered');

      const subjects = {
         RsvpConfirm: 'RSVP Confirmation',
         RsvpInvite: 'You Are Invited!',
         RsvpTellFriend: 'You Are Invited!',
         RsvpGuestInvite: 'You Are Invited!',
         RsvpReminder: 'Your Event Reminder',
         WebinarReminder: 'Your Virtual Event Link',
         PostEventThankyou: 'Thank You For Attending',
         PostEventNotAttend: 'Sorry We Missed You',
         Media: 'Your Event Media',
         Registration: 'You Have Registered'
      };

      this.hasPreview = true;
      this.hasSettings = true;
      this.fonts = this.fontService.pcFonts;

      let brandSettings = this.session.getBrand().settings.experience;

      let defaults = {
         mirrorCampaignFooter: true,
         template: {},
         senderName: this.session.getBrand().name,
         senderEmail: 'no-reply@'+this.session.apiDomain(),
         emailName: typeDef.email_name,
         backgroundColor: brandSettings.backgroundColor,
         contentBackgroundColor: brandSettings.contentBackgroundColor,
         buttonColor: brandSettings.buttonColor,
         buttonTextColor: brandSettings.buttonTextColor,
         font: brandSettings.emailFont,
         fontSize: brandSettings.fontSize,
         fontColor: brandSettings.fontColor,
         footerFontSize: brandSettings.footerFontSize,
         footerFontColor: brandSettings.footerFontColor,
         footerLinkColor: brandSettings.footerLinkColor,
         linkColor: brandSettings.linkColor,
         headerFontSize: brandSettings.headerFontSize,
         extra: { footerText: {} },
         uploads: { headerImage: brandSettings.uploads.emailHeader }
      };

      if (this.isNew) {
         this.experience = {
            campaignId: campaign.id,
            type: typeDef.id,
            settings: ng.copy(defaults)
         };
      }

      // gets default strings for a new experience, or adds new strings on
      // existing experience if a language is added
      l10n.translateObject(
         this.experience.settings,
         'subjectLine',
         subjects[typeDef.email_name],
         this.languages
      );

      this.pages = [new EmailPageModel(this)];
   }

   init() {
      return this.$q.all(_.map(this.languages, lang => {
         let env = this.session.deploymentName() == 'prod' ? 'prod' : 'dev'; // Want to use raw API domain specific response for email templates, but there's no templates for staging yet
         let template = this.typeDef.mandrill_template.replace('-env-', '-' + env + '-');

         if (lang != 'en') {
            template += '-' + lang;
         }

         if (!this.experience.settings.template[lang]) {
            this.experience.settings.template[lang] = template;
         }

         return this.emailApi
            .templateInfo(template)
            .then(result => {
               return this.editorCodeParser(result.code).then(doc => {
                  doc.applyEditableDefaults(this.experience.settings, lang);
                  this.$templateCache.put(template, doc.html);
                  this.templates[lang] = template;
               });
            });
      }));
   }

   test() {
      if (!this.session.getUser().email) {
         this.toast
            .error()
            .message('Your user account does not have an email address.')
            .send();
         return;
      }

      this.openEmailTestDialog().then((fields) => {
         this.experiencesService
            .testEmail(this.experience.id, this.language, {
               ...fields,
               EventType: this.viewOptions.venueType
            })
            .then(() => {
               this.toast
                  .success()
                  .message('An email has been sent to ' + this.session.getUser().email)
                  .send();
            }).catch(() => {
               this.toast
                  .error()
                  .message('Unable to send email')
                  .send();
            });
      });
   }
}

export class EmailPageModel extends EditorPageModel {
   constructor(editor:EmailEditorModel) {
      super(editor);
   }

   get view() {
      return (this.editor as EmailEditorModel).templates[this.editor.language];
   }

   get settings() {
      return this.editor.settings;
   }
}
